import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// ------------------------------------
// --- Utility Functions (Unchanged) ---
// ------------------------------------

/**
 * Utility function to format a number as currency (Indian Rupees)
 * @param {number|string} amount
 * @returns {string} Formatted currency string
 */
const formatCurrency = (amount) => {
    // Ensure amount is treated as a number
    const numericAmount = parseFloat(amount) || 0;
    return `₹${numericAmount.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};

/**
 * Calculates the balance due.
 */
const calculateBalance = (grandTotal, advance) => {
    const totalNum = parseFloat(grandTotal) || 0;
    const advanceNum = parseFloat(advance) || 0;
    // Return the result formatted to two decimal places (as a string)
    return (totalNum - advanceNum).toFixed(2);
};

// ------------------------------------
// --- Main BillingScreen Component ---
// ------------------------------------

const BillingScreen = () => {
    const location = useLocation();
    const orderData = location.state?.orderData;

    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(false);
    }, [orderData]);

    const handlePrint = () => {
        window.print();
    };

    if (loading) {
        return (
            <div className="p-8 text-center text-xl text-gray-700">Loading Bill...</div>
        );
    }

    if (!orderData) {
        return (
            <div className="p-8 text-center text-xl text-red-600">
                No order data found. Please navigate to this screen from the **Order Manager** page.
            </div>
        );
    }

    // --- Data Extraction and Calculation ---

    const {
        id,
        customer_name,
        customer_phone,
        order_date,
        delivery_date,
        // *** FIX: Change 'orderitems' to 'items' to match the data structure from OrderManager ***
        items: orderItemsFromManager,
        total_amount,
        advance_payment,
        tax,
        discount
    } = orderData;

    // Use the correctly named array
    const items = orderItemsFromManager || [];

    // Calculations
    const baseTotal = items.reduce((sum, item) => {
        const unitPrice = parseFloat(item.price) || 0;
        const qty = parseFloat(item.quantity) || 1;
        return sum + (unitPrice * qty);
    }, 0) || parseFloat(total_amount) || 0;

    const advance = parseFloat(advance_payment) || 0;
    const discountAmount = parseFloat(discount) || 0;
    const taxRate = parseFloat(tax) || 0;

    const netAmount = baseTotal - discountAmount;
    const taxAmount = netAmount * (taxRate / 100);
    const grandTotal = netAmount + taxAmount;

    const balanceDue = calculateBalance(grandTotal, advance);
    const billDate = new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });

    // --- Component Rendering (Receipt Format) ---

    return (
        <div className="flex flex-col items-center min-h-screen bg-gray-100 p-4">

            {/* Print Button */}
            <div className="mb-4 no-print">
                <button
                    onClick={handlePrint}
                    className="bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-150"
                >
                    🖨️ **Print Receipt**
                </button>
            </div>

            {/* Bill Container */}
            <div className="w-full max-w-xs bg-white shadow-xl p-4 receipt-print-area">

                <header className="text-center border-b border-dashed pb-2 mb-2">
                    <h1 className="text-lg font-bold">THE TAILOR SHOP</h1>
                    <p className="text-xs">Beezeet Software Solution</p>
                    <p className="text-xs">No. 23, Anna Nagar, Chennai - 600040</p>
                    <p className="text-xs mt-1">GSTIN: XXXXXXXXXXXXXX</p>
                </header>

                <section className="text-xs border-b border-dashed pb-2 mb-2">
                    <div className="flex justify-between">
                        <span>Bill No:</span>
                        <span className="font-bold">ORD-**{id}**</span>
                    </div>
                    <div className="flex justify-between">
                        <span>Bill Date:</span>
                        <span>{billDate}</span>
                    </div>
                    <div className="flex justify-between">
                        <span>Order Date:</span>
                        <span>{order_date}</span>
                    </div>
                    <div className="flex justify-between font-bold text-red-600">
                        <span>Delivery Date:</span>
                        <span>{delivery_date}</span>
                    </div>
                </section>

                <section className="text-xs border-b border-dashed pb-2 mb-2">
                    <p><span className="font-bold">Customer:</span> {customer_name}</p>
                    <p><span className="font-bold">Phone:</span> {customer_phone}</p>

                </section>

                {/* --- ITEM DETAILS SECTION (FIXED TO SHOW COSTUME NAME AND QUANTITY) --- */}
                <section className="text-xs border-b border-dashed pb-2 mb-2">
                    <div className="flex font-bold mb-1">
                        <span className="w-1/2">ITEM</span>
                        <span className="w-1/4 text-right">QTY</span>
                        <span className="w-1/4 text-right">TOTAL</span>
                    </div>
                    {items.map((item, index) => {
                        const unitPrice = parseFloat(item.price) || 0;
                        const qty = parseFloat(item.quantity) || 1;
                        const lineTotal = unitPrice * qty;
                        // Use item.quality or a safe fallback
                        const qualityText = item.quality ? ` (Quality: ${item.quality})` : '';
                        return (
                            <React.Fragment key={index}>
                                {/* Line 1: Costume Name & Quantity/Total */}
                                <div className="flex">
                                    <span className="w-1/2 font-medium">{item.costume_name || 'N/A'}</span> {/* Costume Name */}
                                    <span className="w-1/4 text-right">{qty}</span> {/* Quantity Display */}
                                    <span className="w-1/4 text-right font-medium">{formatCurrency(lineTotal)}</span> {/* Line Total */}
                                </div>
                                {/* Line 2: Quality/Details and Unit Price (for clarity) */}
                                <div className="flex text-gray-600 italic">
                                    {/* Display Quality on its own line if it exists */}
                                    <span className="w-full pl-2 overflow-hidden whitespace-nowrap overflow-ellipsis">
                                        {qualityText}
                                    </span>
                                </div>
                                {/* Line 3: Unit Price */}
                                <div className="flex text-gray-600 italic">

                                    <span className="w-1/2"></span> {/* Empty column */}
                                </div>
                            </React.Fragment>
                        )
                    })}
                </section>
                {/* ---------------------------------------------------- */}

                <section className="text-sm">
                    <div className="flex justify-between font-medium">
                        <span>Sub Total:</span>
                        <span>{formatCurrency(baseTotal)}</span>
                    </div>
                    <div className="flex justify-between text-red-600">
                        <span>Discount:</span>
                        <span>{formatCurrency(-discountAmount)}</span>
                    </div>
                    <div className="flex justify-between border-t mt-1 pt-1">
                        <span className="font-medium">Taxable Amount:</span>
                        <span className="font-bold">{formatCurrency(netAmount)}</span>
                    </div>
                    <div className="flex justify-between border-b pb-1">
                        <span>Tax ({taxRate}%):</span>
                        <span>{formatCurrency(taxAmount)}</span>
                    </div>

                    <div className="flex justify-between font-bold text-lg mt-2">
                        <span>**TOTAL AMOUNT:**</span>
                        <span>**{formatCurrency(grandTotal)}**</span>
                    </div>
                </section>

                <section className="text-sm border-t border-dashed mt-2 pt-2">
                    <div className="flex justify-between">
                        <span className="font-medium">Advance Paid:</span>
                        <span className="text-green-600 font-bold">{formatCurrency(advance)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-red-600 text-lg mt-1">
                        <span>**BALANCE DUE:**</span>
                        <span>**{formatCurrency(balanceDue)}**</span>
                    </div>
                </section>

                <footer className="text-center text-xs mt-4 pt-2 border-t border-dashed">
                    <p>Thank you for your order! 🙏</p>
                    <p className="mt-1">***</p>
                </footer>
            </div>

            {/* Custom CSS for Print Receipt Size and Visibility (Unchanged) */}
            <style dangerouslySetInnerHTML={{
                __html: `
                        @media print {
                            /* Hide everything outside the receipt */
                            body * {
                                visibility: hidden;
                            }
                            .receipt-print-area, .receipt-print-area * {
                                visibility: visible;
                            }
                            .no-print {
                                display: none !important;
                            }
                            
                            /* Focus print area */
                            .receipt-print-area {
                                position: absolute;
                                left: 0;
                                top: 0;
                                margin: 0;
                                padding: 0.5cm; /* Small padding for print margin */
                                width: 80mm; /* Standard POS receipt width */
                                max-width: 100%;
                                box-shadow: none !important;
                            }

                            /* Ensure background is white and text is black */
                            body {
                                background-color: white !important;
                                -webkit-print-color-adjust: exact; /* For webkit browsers */
                                print-color-adjust: exact;
                            }
                            .receipt-print-area * {
                                color: black !important;
                                background-color: white !important;
                            }
                            
                            /* Ensure dashed borders print correctly */
                            .border-dashed {
                                border-style: dashed !important;
                                border-width: 1px !important;
                                border-color: #e5e7eb !important; /* Ensure border color is set for printing */
                            }
                        }
                    `
            }} />
        </div>
    );
};

export default BillingScreen;